import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MODULES } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = { full_name: "", phone: "", email: "", module: "", district: "", message: "" };

export default function ApplyModal({ open, setOpen, preset }) {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ ...empty, module: preset || "" });
      setDone(false);
    }
  }, [open, preset]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.phone || !form.email || !form.module) {
      toast.error("Please fill in name, phone, email and choose a module.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/applications`, form);
      setDone(true);
      toast.success("Application received! Our team will call you soon.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="apply-modal" className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        {done ? (
          <div className="flex flex-col items-center py-8 text-center" data-testid="apply-success">
            <CheckCircle2 size={56} className="text-emerald-green" />
            <h3 className="mt-4 font-heading text-2xl font-bold text-deep-trust-blue">You&apos;re in the queue!</h3>
            <p className="mt-2 text-sm text-deep-trust-blue/65">
              Thank you for applying to the Unnati AI Pilot Program. Our team will reach out on your phone shortly.
            </p>
            <button
              onClick={() => setOpen(false)}
              className="mt-6 rounded-full bg-deep-trust-blue px-6 py-2.5 text-sm font-semibold text-white"
            >
              Done
            </button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl font-extrabold tracking-tight text-deep-trust-blue">
                Join the Pilot Program
              </DialogTitle>
              <DialogDescription className="text-deep-trust-blue/60">
                Free to apply · Voice-first learning · Placement guarantee.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={submit} className="mt-2 space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="full_name">Full Name *</Label>
                <Input
                  id="full_name"
                  data-testid="apply-name"
                  value={form.full_name}
                  onChange={(e) => update("full_name", e.target.value)}
                  placeholder="e.g. Priya Kumari"
                />
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="phone">Phone *</Label>
                  <Input
                    id="phone"
                    data-testid="apply-phone"
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    placeholder="+91 ..."
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="apply-email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder="you@email.com"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label>Choose a Module *</Label>
                <Select value={form.module} onValueChange={(v) => update("module", v)}>
                  <SelectTrigger data-testid="apply-module-select">
                    <SelectValue placeholder="Select a course" />
                  </SelectTrigger>
                  <SelectContent>
                    {MODULES.map((m) => (
                      <SelectItem key={m.id} value={m.title}>
                        {m.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="district">District / Block</Label>
                <Input
                  id="district"
                  data-testid="apply-district"
                  value={form.district}
                  onChange={(e) => update("district", e.target.value)}
                  placeholder="e.g. Ormanjhi, Ranchi"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="message">Anything else? (optional)</Label>
                <Textarea
                  id="message"
                  data-testid="apply-message"
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  placeholder="Tell us about your goals..."
                  rows={3}
                />
              </div>

              <button
                type="submit"
                data-testid="apply-submit-btn"
                disabled={loading}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-vibrant-orange px-6 py-3.5 text-base font-semibold text-white transition-all duration-300 hover:bg-orange-600 disabled:opacity-60"
              >
                {loading && <Loader2 size={18} className="animate-spin" />}
                {loading ? "Submitting..." : "Submit Application"}
              </button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
